import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getMailTrackingDetail,
  mailEventsURL,
  type MailTrackingDetail as Detay,
} from "../api/mailTracking";
import Icon from "../components/icons";
import { ortamCoz, dilCoz, kaynakCoz } from "../lib/ortam";

function fmtSaat(s: string): string {
  try {
    return new Date(s).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "medium" });
  } catch {
    return s;
  }
}

// Olay — zaman çizelgesindeki tek satır. Açılma, tıklama ve sayfa
// görüntülemeleri tek bir akışta birleşir; ziyaretin gerçek sırası ancak
// böyle görülür (önce açtı → tıkladı → şu sayfaları gezdi).
type Olay = {
  anahtar: string;
  zaman: string;
  tur: "acilma" | "tiklama" | "sayfa";
  baslik: string;
  ip?: string;
  ua?: string;
  lang?: string;
  referer?: string;
};

const TUR_ETIKET: Record<Olay["tur"], { metin: string; sinif: string }> = {
  acilma: { metin: "Mail açıldı", sinif: "badge warn" },
  tiklama: { metin: "Siteye giriş", sinif: "badge ok" },
  sayfa: { metin: "Sayfa", sinif: "badge" },
};

export default function MailTrackingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const alicilarID = Number(id);

  const [d, setD] = useState<Detay | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [eskiUstte, setEskiUstte] = useState(true);
  const [canli, setCanli] = useState(false);

  const yukle = useCallback(async (sessiz = false) => {
    if (!Number.isFinite(alicilarID) || alicilarID <= 0) {
      setErr("Geçersiz kayıt");
      setLoading(false);
      return;
    }
    if (!sessiz) setLoading(true);
    try {
      setD(await getMailTrackingDetail(alicilarID));
      setErr(null);
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Yükleme hatası");
    } finally {
      if (!sessiz) setLoading(false);
    }
  }, [alicilarID]);

  useEffect(() => { void yukle(); }, [yukle]);

  // Bu sayfa da canlı: ziyaretçi gezinmeye devam ederken satırlar eklenir.
  useEffect(() => {
    const es = new EventSource(mailEventsURL());
    let z: number | undefined;
    const tazele = () => {
      window.clearTimeout(z);
      z = window.setTimeout(() => void yukle(true), 800);
    };
    es.onopen = () => setCanli(true);
    es.onerror = () => setCanli(false);
    for (const tur of ["open", "click", "pageview"]) es.addEventListener(tur, tazele);
    return () => { window.clearTimeout(z); es.close(); };
  }, [yukle]);

  // Üç kaynağı tek akışta birleştir.
  const olaylar = useMemo<Olay[]>(() => {
    if (!d) return [];
    const liste: Olay[] = [];

    if (d.row.opened_at) {
      liste.push({
        anahtar: "acilma",
        zaman: d.row.opened_at,
        tur: "acilma",
        baslik: d.row.open_count > 1 ? `Mail açıldı (${d.row.open_count} kez görüntülendi)` : "Mail açıldı",
        ip: d.row.open_ip,
        ua: d.row.open_agent,
      });
    }
    for (const c of d.clicks ?? []) {
      liste.push({
        anahtar: `c${c.id}`,
        zaman: c.clicked_at,
        tur: "tiklama",
        baslik: c.url,
        ip: c.ip,
        ua: c.user_agent,
      });
    }
    for (const v of d.pageviews ?? []) {
      liste.push({
        anahtar: `v${v.id}`,
        zaman: v.viewed_at,
        tur: "sayfa",
        baslik: v.path,
        ip: v.ip,
        ua: v.user_agent,
        lang: v.lang,
        referer: v.referer,
      });
    }
    liste.sort((a, b) => {
      const f = new Date(a.zaman).getTime() - new Date(b.zaman).getTime();
      return eskiUstte ? f : -f;
    });
    return liste;
  }, [d, eskiUstte]);

  const sayfaSayisi = (d?.pageviews ?? []).length;
  const tiklamaSayisi = (d?.clicks ?? []).length;

  return (
    <div className="page">
      <div className="page-head">
        <div className="page-head-row">
          <h1>{d?.row.name || d?.row.email || "Ziyaret detayı"}</h1>
          <button className="btn-primary" onClick={() => navigate("/mail-takip")}>
            ← Listeye dön
          </button>
        </div>
        {d && (
          <p className="page-sub">
            <strong>{d.row.email}</strong> ·{" "}
            {d.row.subject ? <>gönderim: “{d.row.subject}”</> : <>kampanya {d.row.campaign}</>} ·{" "}
            {d.row.opened_at ? "mail açıldı" : "mail açılmadı"} · {tiklamaSayisi} giriş ·{" "}
            {sayfaSayisi} sayfa görüntülendi
            <br />
            Aşağıdaki liste ziyaretin tamamıdır: mailin açılması, siteye giriş
            ve ardından gezilen sayfalar zaman sırasıyla. Sayfa görüntülemeleri
            aynı dakika içinde tekrarlanırsa tek satır olarak yazılır (F5
            sayılmaz).
          </p>
        )}
      </div>

      <div className="toolbar">
        <span className="muted">
          {olaylar.length} olay{" "}
          <span
            className={"badge " + (canli ? "ok" : "off")}
            title={canli ? "Yeni olaylar anında düşer" : "Canlı bağlantı yok — Yenile'yi kullanın"}
          >
            {canli ? "canlı" : "bağlantı yok"}
          </span>
        </span>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn-primary" onClick={() => setEskiUstte((v) => !v)}>
            {eskiUstte ? "En yeni üstte ↓" : "En eski üstte ↑"}
          </button>
          <button className="btn-primary" onClick={() => void yukle()}>Yenile</button>
        </div>
      </div>

      {err && <div className="error">{err}</div>}

      <div className="data-table-card">
        {loading ? (
          <div className="empty-state"><div className="spinner-sm" /><p>Yükleniyor…</p></div>
        ) : olaylar.length === 0 ? (
          <div className="empty-state">
            <Icon name="mail" size={34} />
            <p>Bu alıcı için henüz ölçüm kaydı yok.</p>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 40 }}>#</th>
                <th>Zaman</th>
                <th>Olay</th>
                <th>Adres / sayfa</th>
                <th>IP</th>
                <th>Cihaz / tarayıcı</th>
                <th>Dil</th>
                <th>Kaynak</th>
              </tr>
            </thead>
            <tbody>
              {olaylar.map((o, i) => (
                <tr key={o.anahtar}>
                  <td className="muted">{eskiUstte ? i + 1 : olaylar.length - i}</td>
                  <td style={{ whiteSpace: "nowrap" }}>{fmtSaat(o.zaman)}</td>
                  <td><span className={TUR_ETIKET[o.tur].sinif}>{TUR_ETIKET[o.tur].metin}</span></td>
                  <td style={{ maxWidth: 380, wordBreak: "break-all" }}>
                    {o.tur === "acilma" ? <span className="muted">{o.baslik}</span> : o.baslik}
                  </td>
                  <td className="muted" style={{ fontSize: 12 }}>{o.ip || "—"}</td>
                  <td className="muted" style={{ fontSize: 12 }} title={ortamCoz(o.ua).detay}>{ortamCoz(o.ua).ozet}</td>
                  <td className="muted" style={{ fontSize: 12 }}>{dilCoz(o.lang)}</td>
                  <td className="muted" style={{ fontSize: 12 }}>{o.tur === "sayfa" ? kaynakCoz(o.referer, true) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
